<template>
  <div>
    <PageHeaderBanner>
      <template #title>会員情報変更</template>
    </PageHeaderBanner>
    <div class="cmi00200-form-area">
      <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
      <InputFormHeader :list="titleList" :page="page" />
      <q-form autofocus greedy novalidate @submit="page += 1" @validation-error="resetError">
        <div v-if="page === 0">
          <!-- ユーザー情報 -->
          <UserInfoForm :member-info="mKaiinInfo" :mode="DisplayModeCode.CHANGE" />
          <ul>
            <li>
              メールアドレスの変更はできません。<br />
              お手数ですが変更後のメールアドレスにて再度会員登録いただき、
              <router-link :to="{ name: 'inquiry' }" class="text-link">お問い合わせ</router-link>
              より旧アドレスをご連絡ください。
            </li>
            <li>
              退会をご希望の方は、<router-link :to="{ name: 'inquiry' }" class="text-link"
                >お問い合わせ</router-link
              >
              よりご連絡ください。
            </li>
          </ul>
        </div>
        <div v-if="page === 1">
          <!-- 所属先情報 -->
          <AfiliationInfoForm :member-info="mKaiinInfo" />
        </div>
        <div v-if="page === 2">
          <!-- 変更内容確認 -->
          <ConfirmationForm :member-info="mKaiinInfo" />
        </div>
        <FormFooterBanner style="position: inherit">
          <template #button-area>
            <div>
              <!-- ユーザー情報「戻る」 -->
              <q-btn
                v-if="page === 0"
                outline
                :to="{ name: 'memberInfo' }"
                style="color: var(--q-delete); width: 120px"
                class="q-mr-xs"
                label="戻る"
              />
              <!-- 所属先情報、変更内容確認「前へ」 -->
              <q-btn
                v-if="page === 1 || page === 2"
                color="primary"
                outline
                style="width: 120px"
                class="q-mr-xs"
                icon="mdi-chevron-left"
                label="前へ"
                @click="--page"
              />
              <!-- ユーザー情報「次へ」 -->
              <q-btn
                v-if="page === 0"
                color="primary"
                outline
                type="submit"
                style="width: 120px"
                icon-right="mdi-chevron-right"
                label="次へ"
              />
              <!-- 所属先情報「次へ」 -->
              <q-btn
                v-if="page === 1"
                color="primary"
                outline
                type="submit"
                style="width: 120px"
                icon-right="mdi-chevron-right"
                label="次へ"
              />
              <!-- 変更内容確認「変更」 -->
              <q-btn
                v-if="page === 2"
                color="primary"
                style="width: 120px"
                label="変更"
                @click="isConfirmationFlg = true"
              />
            </div>
          </template>
        </FormFooterBanner>
      </q-form>
    </div>
  </div>
  <!-- 更新確認ダイアログ -->
  <AlertDialog
    :display="isConfirmationFlg"
    level="information"
    title="更新確認"
    :text="['入力された内容で更新します。', 'よろしいですか？']"
    @confirm="memberInfoUpdate()"
    @close="isConfirmationFlg = false"
  />
  <!-- 更新完了ダイアログ -->
  <AlertDialog
    :display="updateFlg"
    level="information"
    confirmation
    title="更新完了"
    :text="['更新しました。']"
    @complete="(updateFlg = false), returnMemberInfo()"
  />
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import axios from 'axios';
import { Loading } from 'quasar';
import { useRouter } from 'vue-router';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import AfiliationInfoForm from '../../components/form/member/AfiliationInfoForm.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import ConfirmationForm from '../../components/form/member/ConfirmationForm.vue';
import FormFooterBanner from '../../components/banner/FormFooterBanner.vue';
import InputFormHeader from '../../components/utils/InputFormHeader.vue';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import UserInfoForm from '../../components/form/member/UserInfoForm.vue';
import DisplayModeCode from '../../common/code/DisplayModeCode';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import MessageChip from '../../components/chip/MessageChip.vue';
import * as UserAttributeCode from '../../common/code/UserAttributeCode';

const router = useRouter();

// ページ(0:ユーザー情報 1:所属先情報 2:変更内容確認)
const page = ref(0);
// Stepperに表示するタイトル
const titleList = ['ユーザー情報', '所属先情報', '変更内容確認'];
// 更新確認ダイアログフラグ
const isConfirmationFlg = ref(false);
// 更新完了ダイアログフラグ
const updateFlg = ref(false);
// エラーメッセージ
const errMessage = ref('');
// MessageChip表示フラグ
const isMessageChip = ref(false);
// 会員情報
const mKaiinInfo = reactive({
  user_name_sei: '',
  user_name_mei: '',
  mail_address: '',
  mail_address_conf: '',
  user_attribute: UserAttributeCode.default.REAGENT,
  work_school_name: '',
  affiliation_department_name: '',
  postal_cd: '',
  todohuken_name: '',
  street_address: '',
  phone_number: '',
});

/**
 * エラーチップを初期化
 */
const resetError = async () => {
  errMessage.value = '';
  isMessageChip.value = false;
};

/**
 * 会員情報取得
 */
const getMemberInfo = async () => {
  // Loading Start
  Loading.show();
  errMessage.value = '';
  isMessageChip.value = false;
  // 会員情報取得
  axios
    .post('/cmi001001/memberInfo')
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          mKaiinInfo.user_name_sei = res.data.mkaiin.user_name_sei;
          mKaiinInfo.user_name_mei = res.data.mkaiin.user_name_mei;
          mKaiinInfo.mail_address = res.data.mkaiin.mail_address;
          mKaiinInfo.user_attribute = res.data.mkaiin.user_attribute;
          mKaiinInfo.work_school_name = res.data.mkaiin.work_school_name;
          mKaiinInfo.affiliation_department_name = res.data.mkaiin.affiliation_department_name;
          mKaiinInfo.postal_cd = res.data.mkaiin.postal_cd;
          mKaiinInfo.todohuken_name = res.data.mkaiin.todohuken_name;
          mKaiinInfo.street_address = res.data.mkaiin.street_address;
          mKaiinInfo.phone_number = res.data.mkaiin.phone_number;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          // エラーメッセージ表示
          errMessage.value = common.message;
          isMessageChip.value = true;
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch(() => {
      router.push({ name: 'error' });
    })
    .catch((err) => {
      if (err.res.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 会員情報変更
 */
const memberInfoUpdate = async () => {
  isConfirmationFlg.value = false;
  // Loading Start
  Loading.show();
  errMessage.value = '';
  isMessageChip.value = false;
  // 変更データ作成
  const formData = new FormData();
  formData.append('user_name_sei', mKaiinInfo.user_name_sei);
  formData.append('user_name_mei', mKaiinInfo.user_name_mei);
  formData.append('user_attribute', mKaiinInfo.user_attribute);
  formData.append('work_school_name', mKaiinInfo.work_school_name);
  formData.append('affiliation_department_name', mKaiinInfo.affiliation_department_name);
  formData.append('postal_cd', mKaiinInfo.postal_cd);
  formData.append('todohuken_name', mKaiinInfo.todohuken_name);
  formData.append('street_address', mKaiinInfo.street_address);
  formData.append('phone_number', mKaiinInfo.phone_number);
  // 会員情報変更
  await axios
    .post('/cmi002001/edit', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // ダイアログ表示
          updateFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            // システムエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
            case DetailResultStatusCode.UPDATE_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;
            default:
              router.push({ name: 'error' });
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.res.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 会員情報画面に戻る
 */
const returnMemberInfo = async () => {
  router.push({ name: 'memberInfo' });
};

// 初期表示処理
getMemberInfo();
</script>

<style scoped>
.cmi00200-form-area {
  width: 80%;
  max-width: 800px;
  margin: 0 auto;
}
</style>
